import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import lodashOmit from 'lodash/fp/omit';

import {
  hasRoleAdmin,
  USER_ROLE_ADMIN,
  USER_ROLE_PARTNER,
} from 'common/userRoles';

import {
  DATA_TABLE_SOURCE_PARTNER_MARKET_CONFIGURATIONS,
  DATA_TABLE_VIEW_ID_PARTNER_MARKET_CONFIGURATIONS,
} from 'common/dataTableConstants';

import {
  SR_API_ACTION_PARTNER_SUBMIT_PARTNER_LISTING_SETTINGS,
  SR_API_ACTION_PARTNER_UPDATE_BUYERS_AGENT_COMMISSIONS,
  SR_API_ACTION_PARTNER_UPDATE_MARKET_CONFIGURATIONS,
} from 'api/actionTypes';

import {
  SCREEN_ID_PARTNER_SETTINGS_LISTING,
} from 'client/constants/screenIds';

import postWithState from 'client/actions/postWithState';
import PartnerActions from 'client/actions/Partner';
import PartnerUserActions from 'client/actions/PartnerUser';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, {
  getUserLandingPage,
} from 'client/helpers/allowedRoles';
import {
  actionDataTableReset,
} from 'client/helpers/dataTables';
import branch from 'client/core/branch';

import SrBox from 'client/components/SrBox';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrPartnerListingSettingsForm from 'client/components/SrPartnerListingSettingsForm/SrPartnerListingSettingsForm';
import SrSimpleMessageModal, { SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP } from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_ADMIN,
    USER_ROLE_PARTNER,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_SETTINGS_LISTING)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  buyersAgentCommissionsByPartnerId: [
    'partners',
    'buyersAgentCommissionsByPartnerId',
  ],
  partnerStripeAccounts: ['partners', 'stripeAccountsByPartnerSlug', match.params.partnerSlug],
  partnerUsersByPartnerId: ['partners', 'users', 'listByPartnerId'],
}))
class PartnerSettingsListingPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Settings',
    section: 'Partner',
  })

  static fetchData(match, inject) {
    const { partnerSlug } = match.params;
    let partner = inject.state.get(['partners', 'bySlug', partnerSlug]);

    const promises = [];

    if (!partner) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: partnerSlug,
        }),
      );
    }

    return Promise.all(promises).then(() => {
      partner = inject.state.get(['partners', 'bySlug', partnerSlug]);
      if (partner) {
        return Promise.all([
          PartnerUserActions.getPartnerUserListByPartnerId(inject, {
            partnerId: partner.id,
          }),
          PartnerActions.getPartnerBuyersAgentCommissions(inject, {
            partnerId: partner.id,
          }),
        ]);
      }
    });
  }

  static propTypes = {
    partner: PropTypes.object,
    currentUser: PropTypes.object,
    isTransitioning: PropTypes.bool,
    partnerUsersByPartnerId: PropTypes.object.isRequired,
    buyersAgentCommissionsByPartnerId: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      shouldShowSavedModal: false,
    };
  }

  _onSubmitPartnerListingSettingsForm = (formValues) => {
    const {
      partner,
      currentUser,
      dispatch,
      history,
    } = this.props;
    const {
      buyersAgentCommissions,
      partnerMarketConfigurations,
      ...listingSettings
    } = formValues;

    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_SUBMIT_PARTNER_LISTING_SETTINGS,
      payload: {
        partnerId: partner.id,
        listingSettings: lodashOmit('buyersAgentCommissions', listingSettings),
      },
    })
      .then(() => {
        if (listingSettings.postToMls) {
          return dispatch(postWithState, {
            action: SR_API_ACTION_PARTNER_UPDATE_BUYERS_AGENT_COMMISSIONS,
            payload: {
              partnerId: partner.id,
              buyersAgentCommissions,
            },
          });
        }
      })
      .then(() => {
        if (hasRoleAdmin(currentUser.roles)) {
          return dispatch(postWithState, {
            action: SR_API_ACTION_PARTNER_UPDATE_MARKET_CONFIGURATIONS,
            payload: {
              partnerId: partner.id,
              partnerMarketConfigurations: partnerMarketConfigurations
                .map(({ marketId, partnerApplicationFee }) => ({
                  marketId,
                  updates: {
                    partnerApplicationFee,
                  },
                })),
            },
            onSuccess: () => {
              dispatch(actionDataTableReset, {
                dataSource: DATA_TABLE_SOURCE_PARTNER_MARKET_CONFIGURATIONS,
              });
            },
          });
        }
      })
      .then(() => {
        if (listingSettings.postToMls) {
          return dispatch(PartnerActions.getPartnerBuyersAgentCommissions, {
            partnerId: partner.id,
            reload: true,
          });
        }
      })
      .then(() => {
        dispatch(PartnerActions.loadPartnerBySlug, {
          slug: partner.slug,
          forceRefresh: true,
        });
        this.setState({
          shouldShowSavedModal: true,
        });
        setTimeout(() => {
          history.push(`/partner/${partner.slug}/settings`);
        }, 800);
      });
  }

  render() {
    const {
      partner,
      currentUser,
      isTransitioning,
      partnerUsersByPartnerId,
      buyersAgentCommissionsByPartnerId,
    } = this.props;

    const {
      shouldShowSavedModal,
    } = this.state;

    if (isTransitioning) {
      return null;
    }

    if (!partner) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return (
        <Redirect to={userLandingPage} />
      );
    }

    const filteredPartnerUsers = (partnerUsersByPartnerId[partner.id] || []).filter(
      (x) => x.agentMlsNumber
    );

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${partner.slug}/settings`}
        navbarTitle={partner.displayName}
        pageHeader='Listing & Marketing'
      >
        <SrBox marginBottom='100px'>
          <SrDataTableConnected
            itemsPropName='partnerMarketConfigurations'
            rootViewId={DATA_TABLE_VIEW_ID_PARTNER_MARKET_CONFIGURATIONS}
            dataSource={DATA_TABLE_SOURCE_PARTNER_MARKET_CONFIGURATIONS}
            initialViewSettings={{
              loadAllData: true,
              sort: {
                'partnerMarketConfigurations.marketId': 'asc',
              },
              filter: {
                partnerId: partner.id,
              },
            }}
          >
            {({ partnerMarketConfigurations }) => (
              <SrPartnerListingSettingsForm
                buyersAgentCommissions={(buyersAgentCommissionsByPartnerId[partner.id] || [])}
                onSubmit={this._onSubmitPartnerListingSettingsForm}
                filteredPartnerUsers={filteredPartnerUsers}
                currentUser={currentUser}
                partner={partner}
                partnerMarketConfigurations={partnerMarketConfigurations}
              />
            )}
          </SrDataTableConnected>
        </SrBox>
        {shouldShowSavedModal && (
          <SrSimpleMessageModal
            iconUrl='/images/checkmark-circle-large-green.svg'
            headline='Saved!'
            maxWidth={400}
            minWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

PartnerSettingsListingPage.displayName = 'PartnerSettingsListingPage';

export default PartnerSettingsListingPage;
